import { App } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { PushNotifications } from '@capacitor/push-notifications';
import { Network } from '@capacitor/network';
import { isNative } from './platform';
import { syncOfflineQueues } from './offline';

let initialized = false;

export function hideNativeSplashAfterFirstPaint() {
  if (!isNative()) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      void SplashScreen.hide().catch((error) => {
        console.warn('[Native] SplashScreen hide unavailable', error);
      });
    });
  });
}

function syncViewportHeight() {
  document.documentElement.style.setProperty('--app-viewport-height', `${window.innerHeight}px`);
}

export async function initializeNativeShell() {
  if (!isNative() || initialized) return;
  initialized = true;

  syncViewportHeight();
  window.addEventListener('resize', syncViewportHeight);
  window.addEventListener('orientationchange', syncViewportHeight);

  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#09090b' });
  } catch (error) {
    console.warn('[Native] StatusBar unavailable', error);
  }

  try {
    await SplashScreen.hide();
  } catch (error) {
    console.warn('[Native] SplashScreen unavailable', error);
  }

  try {
    await Keyboard.setAccessoryBarVisible({ isVisible: false });
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.documentElement.style.setProperty('--native-keyboard-height', `${info.keyboardHeight}px`);
    });
    Keyboard.addListener('keyboardDidShow', (info) => {
      document.documentElement.style.setProperty('--native-keyboard-height', `${info.keyboardHeight}px`);
    });
    Keyboard.addListener('keyboardWillHide', () => {
      document.documentElement.style.setProperty('--native-keyboard-height', '0px');
    });
    Keyboard.addListener('keyboardDidHide', () => {
      document.documentElement.style.setProperty('--native-keyboard-height', '0px');
    });
  } catch (error) {
    console.warn('[Native] Keyboard configuration unavailable', error);
  }

  App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) void syncOfflineQueues();
  });

  App.addListener('appUrlOpen', ({ url }) => {
    try {
      const parsed = new URL(url);
      const nativePath = parsed.protocol === 'mindcouncil:' ? `/${parsed.host}${parsed.pathname}` : parsed.pathname;
      const route = `${nativePath}${parsed.search}${parsed.hash}` || '/dashboard';
      window.location.hash = route.startsWith('#') ? route : `#${route}`;
    } catch (error) {
      console.warn('[Native] Failed to route deep link', error);
    }
  });

  Network.addListener('networkStatusChange', (status) => {
    document.documentElement.dataset.network = status.connected ? 'online' : 'offline';
    if (status.connected) void syncOfflineQueues();
  });

  try {
    const status = await Network.getStatus();
    document.documentElement.dataset.network = status.connected ? 'online' : 'offline';
  } catch {
    document.documentElement.dataset.network = 'unknown';
  }
}

export async function requestPushRegistration() {
  if (!isNative()) return { registered: false, reason: 'web' };
  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') return { registered: false, reason: 'permission_denied' };
  await PushNotifications.register();
  return { registered: true };
}
